import { Injectable } from "@angular/core";

import TermType from "../shared/enums/TermsTypes.enum";
import { UnitCustomerTokenDto } from "../shared/models/unit-card/UnitCustomerTokenDto.model";
import { ImpersonateAsUserDetails } from "../models/ImpersonateAsUserDetails.model";

@Injectable({
  providedIn: "root",
})
export class StorageService {
  private AUTHORIZATION_DATA_KEY = "authorizationData";
  private CREDENTIALS_DATA_KEY = "credentialsData";
  private UNIT_CUSTOMER_TOKEN = "UCT";
  private UNIT_WL_COMPONENTS_CUSTOMER_TOKEN = "unitVerifiedCustomerToken"
  private TERMS_DATA_KEY = "_covt";
  private TERMS_RETRY_KEY = "_covrt";
  private DONT_SHOW_ACCREDITATION_CONFIRMATION = "_acct";
  private IMPERSONATE_AS_KEY = "Impersonate-As";
  private readonly GP_REFERRAL_KEY: string = "gprefkey";
  private readonly GP_REFERRAL_KEYS: string = "gprefkeys";
  private readonly OPPORTUNITY_TAGS: string = "opptag";

  private CONFIG_DATA_KEY = "covercy_config";

  // Keys for the Get/Set functions:
  public IS_EXAMPLE_ASSET_REMOVED_LOCAL_STORAGE_KEY =
    "isUserRemovedExampleAsset";

  constructor() {}

  public clear() {
    this.removeAuthorizationData();
    this.removeUnitWLComponentsCustomerToken();
    this.removeCredentialsData();
    localStorage.clear();
    sessionStorage.clear();
    // this.removeUnitCustomerToken();
  }

  private getJsonData(key: string) {
    const json = localStorage.getItem(key);

    if (json) {
      return JSON.parse(json);
    }
    return null;
  }

  // LOCAL STORAGE
  public getPersistent<T>(key: string) {
    const keyValueObject = JSON.parse(
      localStorage.getItem(this.CONFIG_DATA_KEY) || "{}"
    );
    return keyValueObject[key] && (JSON.parse(keyValueObject[key]) as T);
  }

  public hasPersistent(key: string) {
    const keyValueObject = JSON.parse(
      localStorage.getItem(this.CONFIG_DATA_KEY) || "{}"
    );
    return !!keyValueObject[key];
  }

  public setPersistent(key: string, value: any = null) {
    const keyValueObject = JSON.parse(
      localStorage.getItem(this.CONFIG_DATA_KEY) || "{}"
    );

    keyValueObject[key] = JSON.stringify(value);

    localStorage.setItem(this.CONFIG_DATA_KEY, JSON.stringify(keyValueObject));
  }

  public deletePersistent(key: string) {
    const keyValueString = localStorage.getItem(this.CONFIG_DATA_KEY);
    if (!keyValueString) {
      return;
    }

    const keyValueObject = JSON.parse(keyValueString);
    if (!keyValueObject[key]) {
      return;
    }

    delete keyValueObject[key];
    this.updatePersistent(keyValueObject);
  }

  private updatePersistent(value: any) {
    localStorage.setItem(this.CONFIG_DATA_KEY, JSON.stringify(value));
  }

  // SESSION STORAGE
  public getSessionItem<T>(key: string) {
    const keyValueObject = JSON.parse(
      sessionStorage.getItem(this.CONFIG_DATA_KEY) || "{}"
    );
    return keyValueObject[key] && (JSON.parse(keyValueObject[key]) as T);
  }

  public hasSessionItem(key: string) {
    const keyValueObject = JSON.parse(
      sessionStorage.getItem(this.CONFIG_DATA_KEY) || "{}"
    );
    return !!keyValueObject[key];
  }

  public setSessionItem(key: string, value: any = null) {
    const keyValueObject = JSON.parse(
      sessionStorage.getItem(this.CONFIG_DATA_KEY) || "{}"
    );

    keyValueObject[key] = JSON.stringify(value);

    sessionStorage.setItem(
      this.CONFIG_DATA_KEY,
      JSON.stringify(keyValueObject)
    );
  }

  public getAuthorizationData() {
    return this.getJsonData(this.AUTHORIZATION_DATA_KEY);
  }

  public removeAuthorizationData() {
    localStorage.removeItem(this.AUTHORIZATION_DATA_KEY);
  }

  public removeUnitWLComponentsCustomerToken() {
    localStorage.removeItem(this.UNIT_WL_COMPONENTS_CUSTOMER_TOKEN);
  }

  public setAuthorizationData(data: string) {
    localStorage.setItem(this.AUTHORIZATION_DATA_KEY, data);
  }

  public getCredentialsData() {
    return this.getJsonData(this.CREDENTIALS_DATA_KEY);
  }

  public removeCredentialsData() {
    localStorage.removeItem(this.CREDENTIALS_DATA_KEY);
  }

  public setCredentialsData(data: string) {
    localStorage.setItem(this.CREDENTIALS_DATA_KEY, data);
  }

  public setTermsData(data: TermType[]) {
    localStorage.setItem(this.TERMS_DATA_KEY, JSON.stringify(data));
  }

  public getTermsData(): TermType[] {
    return this.getJsonData(this.TERMS_DATA_KEY);
  }

  public removeTermsData() {
    localStorage.removeItem(this.TERMS_DATA_KEY);
  }

  public setTermsApproveRetry() {
    localStorage.setItem(this.TERMS_RETRY_KEY, JSON.stringify(true));
  }

  public getTermsApproveRetry() {
    return localStorage.getItem(this.TERMS_RETRY_KEY);
  }

  public removeTermsApproveRetry() {
    localStorage.removeItem(this.TERMS_RETRY_KEY);
  }

  public getUnitCustomerToken(unitCustomerId: number): UnitCustomerTokenDto {
    return this.getJsonData(
      `${this.UNIT_CUSTOMER_TOKEN}_${unitCustomerId}`
    ) as UnitCustomerTokenDto;
  }

  public removeUnitCustomerToken() {
    localStorage.removeItem(this.UNIT_CUSTOMER_TOKEN);
  }

  public setUnitCustomerToken(
    data: UnitCustomerTokenDto,
    unitCustomerId: number
  ) {
    localStorage.setItem(
      `${this.UNIT_CUSTOMER_TOKEN}_${unitCustomerId}`,
      JSON.stringify(data)
    );
  }

  public setImpersonateAsData(impersonationData: ImpersonateAsUserDetails) {
    localStorage.setItem(
      this.IMPERSONATE_AS_KEY,
      JSON.stringify(impersonationData)
    );
  }

  public getImpersonateAsData(): ImpersonateAsUserDetails {
    const json = localStorage.getItem(this.IMPERSONATE_AS_KEY);
    return !!json ? JSON.parse(json) : null;
  }

  public setAccreditationDontShowConfirmation(data: boolean) {
    localStorage.setItem(
      this.DONT_SHOW_ACCREDITATION_CONFIRMATION,
      JSON.stringify(data)
    );
  }

  public getAccreditationDontShowConfirmation(): boolean {
    return !!this.getJsonData(this.DONT_SHOW_ACCREDITATION_CONFIRMATION);
  }

  public removeAccreditationDontShowConfirmation() {
    localStorage.removeItem(this.DONT_SHOW_ACCREDITATION_CONFIRMATION);
  }

  public setGpReferralKeyDataInStorage(data: string) {
    this.removeGpReferralKeyDataFromStorage();
    localStorage.setItem(this.GP_REFERRAL_KEY, JSON.stringify(data));
  }

  public getGpReferralKeyDataFromStorage(): string {
    const gpRefKey = this.getJsonData(this.GP_REFERRAL_KEY);
    return gpRefKey;
  }

  public removeGpReferralKeyDataFromStorage() {
    localStorage.removeItem(this.GP_REFERRAL_KEY);
  }

  public updateGpReferralKeysDataInStorage(data: string) {
    let gpRefKeys = this.getAllGpReferralKeysDataFromStorage();

    if (!gpRefKeys) {
      gpRefKeys = [data];
    } else {
      gpRefKeys.push(data);
    }

    localStorage.setItem(this.GP_REFERRAL_KEYS, JSON.stringify(gpRefKeys));
  }

  public getAllGpReferralKeysDataFromStorage(): string[] {
    const gpRefKeys = this.getJsonData(this.GP_REFERRAL_KEYS);
    return gpRefKeys;
  }

  public updateOpportunityTagsDataInStorage(data: string) {
    let tags = this.getAllOpportunityTagsDataFromStorage();

    if (!tags) {
      tags = [data];
    } else {
      tags.push(data);
    }

    localStorage.setItem(this.OPPORTUNITY_TAGS, JSON.stringify(tags));
  }

  public getAllOpportunityTagsDataFromStorage(): string[] {
    const tags = this.getJsonData(this.OPPORTUNITY_TAGS);
    return tags;
  }

  public removeAllOpportunityTagsFromStorage() {
    localStorage.removeItem(this.OPPORTUNITY_TAGS);
  }
}
